import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  ReceiveTicketDistributionMutation,
  ReceiveTicketDistributionMutationVariables,
  TicketDistributionLinkQuery,
  TicketDistributionLinkQueryVariables,
  TicketShareConfirmFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { getSerialFormatNumber } from '@/components/organisms'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { encodeSkuId } from '@/core/sku'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { showError, showMessage } = useMessage()
  const { t } = useTranslation('ticketShareConfirm')

  const { router, generatePath, routerPages } = useRouter<{
    token: string
  }>()

  const { data, loading } = useQuery<
    TicketDistributionLinkQuery,
    TicketDistributionLinkQueryVariables
  >(QUERY, {
    variables: {
      token: router.query.token,
    },
  })

  const [receiveTicketDistribution, { loading: receiving }] = useMutation<
    ReceiveTicketDistributionMutation,
    ReceiveTicketDistributionMutationVariables
  >(RECEIVE_TICKET_DISTRIBUTION)

  const invalidTicketDistribution = useMemo(
    () =>
      !!data?.ticketDistributionLink?.isExpired ||
      !!data?.ticketDistributionLink?.userId ||
      !!data?.ticketDistributionLink?.item.ownedCount,
    [
      data?.ticketDistributionLink?.isExpired,
      data?.ticketDistributionLink?.userId,
      data?.ticketDistributionLink?.item.ownedCount,
    ]
  )

  const ticketNumber = useMemo(() => {
    return getSerialFormatNumber(data?.ticketDistributionLink?.serial)
  }, [data?.ticketDistributionLink?.serial])

  return {
    state: {
      loading,
      receiving,
      data: data?.ticketDistributionLink ?? null,
      formattedExpiredAt:
        data?.ticketDistributionLink?.formattedExpiredAt ?? null,
      invalidTicketDistribution,
      ticketNumber,
    },
    actions: {
      async onReceive() {
        await receiveTicketDistribution({
          variables: {
            inputs: {
              token: router.query.token,
            },
          },
        })
          .then(() => {
            router.replace({
              pathname: generatePath(routerPages.OwnedItemDetail, {
                itemId: data?.ticketDistributionLink?.item?.id,
              }),
              search: data?.ticketDistributionLink?.serial?.sku?.id
                ? `?skuId=${encodeSkuId(
                    data.ticketDistributionLink.serial.sku.id
                  )}`
                : '',
            })
            showMessage(t('receivedTicketSuccess'))
          })
          .catch(showError)
      },
    },
  }
})
const QUERY = gql`
  query TicketDistributionLink($token: String!) {
    ticketDistributionLink(token: $token) {
      ...TicketShareConfirm
    }
  }
  ${TicketShareConfirmFragmentDoc}
`

const RECEIVE_TICKET_DISTRIBUTION = gql`
  mutation ReceiveTicketDistribution($inputs: ReceiveTicketDistributionInput!) {
    receiveTicketDistribution(inputs: $inputs) {
      id
      distributionId
    }
  }
`
